/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

const normalizeMessageList = (messages) => {
  if (!messages) return [];
  if (Array.isArray(messages)) return messages;
  if (typeof messages === 'object') return [messages];
  return [];
};

const normalizeBase64Input = (value) => value.trim().replace(/-/g, '+').replace(/_/g, '/');

const looksLikeBase64Alphabet = (value) => {
  const normalized = normalizeBase64Input(value);
  return /^[A-Za-z0-9+/]+={0,2}$/.test(normalized) && normalized.length % 4 === 0;
};

const decodeBase64ToUtf8 = (value) => {
  const base64 = normalizeBase64Input(value);
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = window.atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
};

const encodeUtf8ToBase64 = (value) => {
  const updatedData = new TextEncoder().encode(value);
  return window.btoa(String.fromCharCode(...updatedData));
};

const isStrictBase64 = (value) => {
  if (value == null || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;

  const normalized = normalizeBase64Input(trimmed);
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(normalized)) return false;
  if (normalized.length % 4 !== 0) return false;

  // Longer unpadded strings are treated as encoded prompt payloads from save.
  if (normalized.length < 8 && !normalized.includes('=')) return false;

  try {
    return window.btoa(window.atob(normalized)) === normalized;
  } catch {
    return false;
  }
};

/**
 * Decode inline prompt text from API/DB. Handles short base64 (e.g. "eWVz" → "yes")
 * using round-trip verification so arbitrary short plain text is left unchanged.
 */
export const decodePromptContent = (value) => {
  if (value == null || value === '') return '';
  if (typeof value !== 'string') return String(value);
  if (!looksLikeBase64Alphabet(value)) return value;

  try {
    const decoded = decodeBase64ToUtf8(value);
    if (isStrictBase64(value)) return decoded;

    const normalized = normalizeBase64Input(value);
    if (encodeUtf8ToBase64(decoded) === normalized) return decoded;
    return value;
  } catch {
    return value;
  }
};

const getMessageText = (message) => {
  if (!message) return '';
  const content = decodePromptContent(message.content);
  if (content) return content;
  return decodePromptContent(message.context);
};

export const getSystemPromptFromDocument = (doc) => {
  const start = normalizeMessageList(doc?.promptStart);
  const systemMsg = start.find((m) => m?.role === 'system');
  if (systemMsg) return getMessageText(systemMsg);
  return getMessageText(start[0]);
};

export const getUserPromptFromDocument = (doc) => {
  const end = normalizeMessageList(doc?.promptEnd);
  const userMsg = end.find((m) => m?.role === 'user');
  if (userMsg) return getMessageText(userMsg);
  return getMessageText(end[end.length - 1]);
};

const mapPromptMessages = (messages) =>
  normalizeMessageList(messages).map((message) => ({
    ...message,
    content: decodePromptContent(message?.content),
    ...(message?.context != null ? { context: decodePromptContent(message.context) } : {})
  }));

export const mapResolvedDocument = (data) => {
  if (!data) return null;
  return {
    id: data.id || data.promptRef || null,
    version: data.version || data.promptVersionRef || null,
    status: data.status || null,
    name: data.name || null,
    comments: data.comments || null,
    description: data.description || null,
    promptStart: mapPromptMessages(data.promptStart),
    promptEnd: mapPromptMessages(data.promptEnd),
    createdDate: data.createdDate,
    updatedDate: data.updatedDate != null ? String(data.updatedDate) : null,
    modifiedBy: data.modifiedBy || null
  };
};

export default {
  decodePromptContent,
  getSystemPromptFromDocument,
  getUserPromptFromDocument,
  mapResolvedDocument
};
